import clsx from 'clsx';
import { select } from 'd3';
import { useLayoutEffect, useMemo, useRef } from 'react';

import type { ColourScale } from '~utils';
import { Circle, useSvgContext } from '../components';
import { useD3SvgSelection } from '../hooks';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

/** Minimum hole shape required by this layer. */
type BaseHole = { hole_name: string };

type Coords = { x: number; y: number };

type DrillHolesLayerBaseProps<THole extends BaseHole> = {
  /** Holes to render */
  drillHoles: THole[];
  /** Extract planned collar coordinates from a hole */
  getPlannedCoords: (hole: THole) => Coords | null;
  /** Extract actual collar coordinates from a hole */
  getActualCoords: (hole: THole) => Coords | null;
  /** Extract the value of a named property from a hole for colour scaling */
  getPropertyValue?: (hole: THole, property: string) => unknown;
  /** Optional colour scale */
  colourScale?: ColourScale;
  /**
   * Derive a stable, unique identifier from a hole for selection tracking.
   * Defaults to `hole.hole_name` — override when `hole_name` is not unique
   * across the rendered set (e.g. neighbouring holes from multiple patterns).
   */
  getHoleId?: (hole: THole) => string;
  /** Currently selected hole identifier */
  selectedHoleId: string | null;
  /** Called when a hole is activated/selected */
  onHoleSelect: (hole: THole) => void;
  /** Optional formatter for the title attribute of each hole */
  titleFormatter?: (hole: THole) => string;
  /** Whether to render planned or actual coordinates */
  plannedOrActualCoordinates?: 'planned' | 'actual';
  /** Type of drill holes */
  type?: 'drilled' | 'neighbouring';
  /** Optional CSS class name for the layer group */
  className?: string;
};

/* -------------------------------------------------------------------------- */
/*                                 Constants                                  */
/* -------------------------------------------------------------------------- */

/** Data attribute used by drill hole circles and the D3 selection hook */
const DATA_ATTR = 'data-hole-name' as const;

/** CSS classes for Circle — hover/selected/invalid styling handled declaratively */
const HOLE_CLASSES = [
  'hover:scale-[1.5]',
  'group-focus-visible:scale-[1.5]',
  'group-focus-visible:stroke-(--color-primary)',
  'group-focus-visible:stroke-4',
  'group-[&.selected]:scale-[1.5]',
  'group-[&.selected]:stroke-4!',
  'group-[&.selected]:stroke-primary',
].join(' ');

/** Invalid hole classes — dashed stroke via CSS */
const INVALID_HOLE_CLASSES = [HOLE_CLASSES, '[&_.hole-circle]:stroke-dash-4'].join(' ');

/* -------------------------------------------------------------------------- */
/*                             Helper functions                                */
/* -------------------------------------------------------------------------- */

/**
 * Build SVG-safe coordinates.
 * Returns null if either value is null or undefined.
 * Uses explicit null checks — avoids treating a valid coordinate of 0 as missing.
 */
const buildCoords = (x?: number, y?: number): Coords | null => (x == null || y == null ? null : { x, y });

/** Apply selected/deselected visual state to a drill hole group via D3. */
const applySelected = (group: SVGGElement, isSelected: boolean) => {
  const g = select(group);
  g.classed('selected', isSelected).attr('aria-pressed', isSelected ? 'true' : 'false');
};

/** Build a lookup map from id → THole using the provided key function. */
const buildHoleLookup = <THole extends BaseHole>(
  drillHoles: THole[],
  getHoleId: (hole: THole) => string,
): Map<string, THole> => {
  const map = new Map<string, THole>();
  for (const hole of drillHoles) {
    const id = getHoleId(hole);
    if (id) map.set(id, hole);
  }
  return map;
};

/**
 * Resolve the fill colour for a single hole.
 * Invalid holes get transparent fill; valid holes use the colour scale or a default.
 */
const resolveHoleFill = <THole extends BaseHole>(
  hole: THole,
  isInvalid: boolean,
  getPropertyValue: ((hole: THole, property: string) => unknown) | undefined,
  colourScale?: ColourScale,
): string => {
  if (isInvalid) return 'transparent';
  if (!colourScale) return 'var(--color-primary)';

  const propertyValue = getPropertyValue?.(hole, colourScale.property) ?? null;
  if (propertyValue === null) return 'var(--color-primary)';

  if (colourScale.type === 'discrete') {
    return colourScale.getColour(String(propertyValue)).fill;
  }

  return colourScale.getColour(Number(propertyValue));
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Generic base layer for rendering drill holes as SVG circles.
 *
 * React owns the DOM structure (one `<Circle>` per hole); D3 owns
 * the imperative mutations for fill colour and selection styling.
 * Hover and selected visual states are handled via CSS classes on `Circle`.
 *
 * Uses collar coordinates (planned or actual) for positioning, with a fallback
 * from actual → planned when actual coordinates are missing.
 *
 * Not exported publicly — use the typed adapter components instead:
 * `DrilledDrillHolesLayer` and `NeighbouringDrillHolesLayer`.
 */
const DEFAULT_GET_HOLE_ID = <THole extends BaseHole>(hole: THole) => hole.hole_name;

const DrillHolesLayerBaseInner = <THole extends BaseHole>({
  drillHoles,
  getPlannedCoords,
  getActualCoords,
  getPropertyValue,
  colourScale,
  getHoleId = DEFAULT_GET_HOLE_ID,
  selectedHoleId,
  onHoleSelect,
  titleFormatter,
  plannedOrActualCoordinates = 'actual',
  type = 'drilled',
  className,
}: DrillHolesLayerBaseProps<THole>) => {
  const layerRef = useRef<SVGGElement | null>(null);
  const { transformer } = useSvgContext();

  const holeByName = useMemo(() => buildHoleLookup(drillHoles, getHoleId), [drillHoles, getHoleId]);

  // Transform collar coordinates to SVG coordinates and compute tooltips
  const { transformed, tooltips } = useMemo(() => {
    if (!transformer) return { transformed: [], tooltips: [] };

    const entries = drillHoles.reduce<{ hole: THole; isInvalid: boolean; x: number; y: number }[]>((acc, hole) => {
      let coords = plannedOrActualCoordinates === 'planned' ? getPlannedCoords(hole) : getActualCoords(hole);

      const isInvalid = !coords;

      // Fallback to planned coords so we can still show the hole on the map
      if (isInvalid && plannedOrActualCoordinates === 'actual') {
        coords = getPlannedCoords(hole);
      }

      if (!coords) return acc;

      const svgCoords = transformer.toSVG(coords.x, coords.y);
      acc.push({ hole, isInvalid, x: svgCoords.x, y: svgCoords.y });
      return acc;
    }, []);

    return {
      transformed: entries,
      tooltips: titleFormatter ? entries.map(({ hole }) => titleFormatter(hole)) : [],
    };
  }, [drillHoles, transformer, plannedOrActualCoordinates, getPlannedCoords, getActualCoords, titleFormatter]);

  // Stable lookup function — rebuilds only when the map changes
  const getItemById = useMemo(() => {
    return (id: string) => holeByName.get(id) ?? null;
  }, [holeByName]);

  useD3SvgSelection<THole>({
    layerRef,
    dataAttribute: DATA_ATTR,
    selectedId: selectedHoleId,
    getItemById,
    onSelect: onHoleSelect,
    applySelected,
  });

  // Imperatively apply fill colours via D3 (avoids re-rendering every Circle on colour change)
  // Invalid holes always have transparent fill
  // Valid holes use the colour scale if available, otherwise default to 'var(--color-primary)'
  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (!layer || transformed.length === 0) return;

    select(layer)
      .selectAll<SVGGElement, unknown>(`[${DATA_ATTR}]`)
      .each(function () {
        const holeId = this.getAttribute(DATA_ATTR);
        if (!holeId) return;

        const hole = holeByName.get(holeId);
        if (!hole) return;

        const isInvalid = this.getAttribute('data-invalid') === 'true';
        const fill = resolveHoleFill(hole, isInvalid, getPropertyValue, colourScale);

        select(this).select<SVGCircleElement>('.hole-circle').attr('fill', fill);
      });
  }, [transformed, holeByName, colourScale, getPropertyValue]);

  // Memoize markup to prevent re-rendering all circles on every prop change
  const holesMarkup = useMemo(
    () =>
      transformed.map(({ hole, isInvalid, x, y }, index) => (
        <Circle
          // biome-ignore lint/suspicious/noArrayIndexKey: getHoleId output is not guaranteed unique (duplicate entries in API response) — index is the only safe React key.
          key={`${getHoleId(hole)}-${index}`}
          name={getHoleId(hole)}
          className={isInvalid ? INVALID_HOLE_CLASSES : HOLE_CLASSES}
          stroke={isInvalid ? 'var(--color-destructive)' : undefined}
          fill="var(--color-primary)"
          radius={1}
          strokeWidth={1}
          transform={`translate(${x}, ${y})`}
          title={tooltips[index]}
          dataAttrs={{ invalid: isInvalid ? 'true' : undefined }}
        />
      )),
    [transformed, tooltips, getHoleId],
  );

  return (
    <g ref={layerRef} className={clsx(`${type}-drill-holes`, className)}>
      {holesMarkup}
    </g>
  );
};
const DrillHolesLayerBase = DrillHolesLayerBaseInner;

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { BaseHole, Coords, DrillHolesLayerBaseProps };
export { buildCoords, DrillHolesLayerBase };
